/* eslint-disable max-lines, max-lines-per-function */
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Typography,
  Divider,
  Chip,
  Link as MLink,
} from "@mui/material";
// eslint-disable-next-line import/no-unresolved
import MaterialTable, { Action, Column } from "@material-table/core";
import { gql, useQuery } from "@apollo/client";
import {
  mergePropertyContractUpdatedIntoList,
  PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
} from "data/graphql/property-contract-updated-subscription";
import { AdminPropertyContracts, plansAll } from "types/gql-types";
import { GET_PLANS_ALL } from "../plans/queries";
import { priceDisplayFor } from "./contract-pricing";
import { formatDate, formatDateOnly, formatSignedAt } from "./contract-dates";

export const CONTRACTS_QUERY = gql`
  query AdminPropertyContracts {
    propertyContracts {
      id
      manuallyEntered
      propertyId
      propertyName
      propertyLegalName
      unitCount
      propertyPhone
      propertyEmail
      propertyAddress
      propertyCityStateZip
      inDevelopment
      accountEmail
      planId
      planName
      pricePerUnit
      billingFrequency
      signerName
      signerEmail
      status
      sentAt
      signedAt
      contractStartDate
      initialTermEndDate
      pdfUrl
    }
  }
`;

type Row = AdminPropertyContracts["propertyContracts"][number];
type PlanRow = plansAll["plansAll"][number];
type DraftSigned = "Draft" | "Signed";
type YesNo = "Yes" | "No";
type TableRow = Row & {
  draftSigned: DraftSigned;
  priceDisplay: string;
  manuallyEnteredLabel: YesNo;
};

const draftSignedFromStatus = (status: string | null): DraftSigned => (
  status === "signed" ? "Signed" : "Draft"
);

const renderAddressCell = (row: Row) => {
  const addr = row.propertyAddress;
  const cityStateZip = row.propertyCityStateZip;
  return (
    <span>
      {addr || "-"}
      {cityStateZip ? (
        <>
          <br />
          {cityStateZip}
        </>
      ) : null}
    </span>
  );
};

const renderInDevCell = (value: boolean | null) => {
  if (value) return <Chip size="small" color="warning" label="Yes" />;
  return <span>No</span>;
};

const renderManuallyEnteredCell = (value: boolean | null) => {
  if (value) return <Chip size="small" color="info" label="Yes" />;
  return <span>No</span>;
};

const renderPdfCell = (url: string | null) => {
  if (url) {
    return (
      <MLink href={url} target="_blank" rel="noopener noreferrer">
        <Button size="small" variant="outlined">Download</Button>
      </MLink>
    );
  }
  return <span>-</span>;
};

/** Shown by default; others use `hidden: true` (toggle via toolbar “columns” control). */
const columns: Column<TableRow>[] = [
  {
    title: "Property Legal Name",
    field: "propertyLegalName",
    emptyValue: "-",
  },
  { title: "Signer", field: "signerName" },
  { title: "Signer Email", field: "signerEmail" },
  {
    title: "Draft vs Signed",
    field: "draftSigned",
    lookup: {
      Draft: "Draft",
      Signed: "Signed",
    },
  },
  {
    title: "Manually Entered",
    field: "manuallyEnteredLabel",
    render: (rowData: TableRow) => renderManuallyEnteredCell(rowData.manuallyEntered),
    lookup: { Yes: "Yes", No: "No" },
  },
  { title: "Status", field: "status", hidden: true },
  {
    title: "PDF",
    field: "pdfUrl",
    render: (rowData: TableRow) => renderPdfCell(rowData.pdfUrl),
    sorting: false,
  },
  { title: "Property", field: "propertyName", hidden: true },
  { title: "Units", field: "unitCount", emptyValue: "-", hidden: true },
  {
    title: "Property Phone",
    field: "propertyPhone",
    emptyValue: "-",
    hidden: true,
  },
  {
    title: "Property Email",
    field: "propertyEmail",
    emptyValue: "-",
    hidden: true,
  },
  {
    title: "Property Address",
    field: "propertyAddress",
    hidden: true,
    render: (rowData: Row) => renderAddressCell(rowData),
  },
  {
    title: "In Development",
    field: "inDevelopment",
    hidden: true,
    render: (rowData: Row) => renderInDevCell(rowData.inDevelopment),
  },
  {
    title: "Account Email",
    field: "accountEmail",
    emptyValue: "-",
    hidden: true,
  },
  { title: "Plan ID", field: "planId", emptyValue: "-", hidden: true },
  {
    title: "Price / Unit",
    field: "priceDisplay",
    hidden: true,
  },
  {
    title: "Billing Frequency",
    field: "billingFrequency",
    emptyValue: "-",
    hidden: true,
  },
  {
    title: "Sent",
    field: "sentAt",
    hidden: true,
    render: (rowData: TableRow) => formatDate(rowData.sentAt),
  },
  {
    title: "Signed",
    field: "signedAt",
    hidden: true,
    render: formatSignedAt,
  },
  {
    title: "Contract Start",
    field: "contractStartDate",
    hidden: true,
    render: (rowData: TableRow) => formatDateOnly(rowData.contractStartDate),
  },
  {
    title: "Contract End",
    field: "initialTermEndDate",
    hidden: true,
    render: (rowData: TableRow) =>
      formatDateOnly(rowData.initialTermEndDate),
  },
];

const AdminContractsPage: React.FC = () => {
  const navigate = useNavigate();
  // cache-and-network: revalidate on every mount so returning from the
  // new/edit pages reflects the latest data (the list is unmounted while
  // editing, so the mutation's refetchQueries cannot update it in place).
  const { data, loading, error, subscribeToMore } = useQuery<AdminPropertyContracts>(
    CONTRACTS_QUERY,
    { fetchPolicy: "cache-and-network", nextFetchPolicy: "cache-first" },
  );

  React.useEffect(() => {
    if (!data?.propertyContracts) {
      return () => {};
    }
    const unsubscribe = subscribeToMore({
      document: PROPERTY_CONTRACT_UPDATED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) =>
        mergePropertyContractUpdatedIntoList(prev, subscriptionData),
    });
    return () => {
      unsubscribe();
    };
  }, [data?.propertyContracts, subscribeToMore]);
  const { data: plansData } = useQuery<plansAll>(GET_PLANS_ALL, {
    fetchPolicy: "cache-first",
  });
  const contracts: Row[] = data?.propertyContracts || [];
  const plansById = React.useMemo(() => {
    const map = new Map<string, PlanRow>();
    (plansData?.plansAll || []).forEach((plan) => {
      map.set(plan.id, plan);
    });
    return map;
  }, [plansData?.plansAll]);
  // Not memoized: Apollo subscribeToMore can update cache without changing the
  // `propertyContracts` array reference; MaterialTable only syncs props.data when
  // deep-eql sees a new data shape, so we must build a fresh rows array each render.
  const tableRows: TableRow[] = contracts.map((contract) => ({
    ...contract,
    draftSigned: draftSignedFromStatus(contract.status),
    priceDisplay: priceDisplayFor(contract, plansById),
    manuallyEnteredLabel: contract.manuallyEntered ? "Yes" : "No",
  }));

  return (
    <div>
      <Typography variant="h5">Contracts</Typography>
      <Divider style={{ margin: "8px 0 16px" }} />
      {error && (
        <Typography variant="body2" color="error" gutterBottom>
          {error.message}
        </Typography>
      )}
      {loading && !data?.propertyContracts ? <Typography variant="body1">Loading…</Typography> : (
        <MaterialTable
          title=""
          columns={columns}
          data={tableRows}
          actions={[
            {
              icon: "add",
              tooltip: "Add Contract",
              isFreeAction: true,
              onClick: () => navigate("/dash/admin/contracts/new"),
            },
            (row: TableRow): Action<TableRow> => ({
              icon: "edit",
              tooltip: "Edit Contract",
              hidden: !row.manuallyEntered,
              onClick: () => navigate(`/dash/admin/contracts/${row.id}/edit`),
            }),
          ]}
          options={{
            pageSize: 25,
            pageSizeOptions: [10, 25, 50, 100],
            sorting: true,
            padding: "dense",
            search: true,
            filtering: true,
            columnsButton: true,
          }}
        />
      )}
    </div>
  );
};

export default AdminContractsPage;
